import React, { useEffect, useMemo, useState } from 'react';
import { Disclosure } from '@headlessui/react';
import { t } from 'ttag';

import { Button } from '@components';
import Membership from '../../../modules/Membership/Membership';
import GiftCard from '../../../modules/GiftCard/GiftCard';
import { useNavigation } from '../context/NavigationContext';

export default function Navigation() {
  const { navigationItems } = useNavigation();

  const [USPs, setUSPs] = useState([]);

  const USPType = useMemo(() => 'signup_unisport_fc_-_usp', []);

  useEffect(() => {
    const fetchUSPs = async () => {
      const response = await fetch(`/api/get_usp/?type=${USPType}`);
      const data = await response.json();

      if (data && data[USPType]) {
        setUSPs(data[USPType].slice(0, 3));
      }
    };
    fetchUSPs();
  }, []);

  const filteredNavigationItems = navigationItems
    .filter(item => item.online && item.base_item_id !== 1731)
    .map(item => {
      const filteredSubItems = item.items.filter(subItem => subItem.online);
      return { ...item, items: filteredSubItems };
    });

  const giftCardHref = navigationItems?.find?.(
    item => item.base_item_id === 1731 && item.online
  )?.slug;

  function normalizeUrl(url) {
    if (/^https?:\/\//.test(url)) {
      return url;
    }
    return url.startsWith('/') ? url : `/${url}`;
  }

  return (
    <section className="my-16 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-24">
      <div className="flex h-fit flex-wrap gap-4 md:flex-nowrap lg:flex-wrap xl:flex-nowrap">
        <Membership className="grow" withBackground>
          <Membership.Heading>{t`Become a Member Today`}</Membership.Heading>
          {USPs && (
            <Membership.USPs>
              {USPs.map(({ headline }) => (
                <Membership.USP key={headline}>{headline}</Membership.USP>
              ))}
            </Membership.USPs>
          )}
          <div className="flex gap-x-2">
            <Membership.Button as="a" href="/account/login/" className="w-full">
              {t`Join for Free`}
            </Membership.Button>
          </div>
        </Membership>
        {giftCardHref && (
          <GiftCard className="grow" href={`/${giftCardHref || ''}`} />
        )}
      </div>

      <div className="min-h-[192px] lg:hidden">
        {filteredNavigationItems?.map(
          ({ title, items, slug, base_item_id: itemId }) =>
            title && items?.length > 0 ? (
              <Disclosure key={itemId}>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex w-full justify-between border-none bg-transparent p-0 pt-4 text-left text-base font-bold focus:outline-none">
                      <span>{title}</span>

                      <svg
                        className={`${
                          open ? 'rotate-180 transform' : ''
                        } h-5 w-5`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                      >
                        <path d="M11.9997 13.1714L16.9495 8.22168L18.3637 9.63589L11.9997 15.9999L5.63574 9.63589L7.04996 8.22168L11.9997 13.1714Z" />
                      </svg>
                    </Disclosure.Button>
                    <Disclosure.Panel className="pb-2 pt-4">
                      {items?.map(
                        ({
                          title: itemTitle,
                          slug: subItemSlug,
                          base_item_id: subItemId
                        }) => (
                          <a
                            key={subItemId}
                            className="block pt-4 text-sm font-bold text-dark-gray"
                            href={normalizeUrl(subItemSlug)}
                          >
                            {itemTitle}
                          </a>
                        )
                      )}
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ) : (
              <Button
                key={itemId}
                variant="tertiary"
                as="a"
                href={`/${slug}`}
                className="m-0 p-0"
              >
                <Button.Text className="p-0 pt-4 text-left text-base font-bold">
                  {title}
                </Button.Text>
              </Button>
            )
        )}
      </div>
      <div className="hidden lg:flex lg:items-center lg:justify-center">
        <div className="grid grid-cols-3 gap-8">
          {filteredNavigationItems?.map(
            ({ title, items, base_item_id: itemId, slug }) => (
              <div key={itemId}>
                {title && items.length > 0 ? (
                  <>
                    <h2 className=" m-0 text-lg font-bold">{title}</h2>
                    <ul className="">
                      {items?.map(
                        ({
                          title: itemTitle,
                          slug: subItemSlug,
                          base_item_id: subItemId
                        }) => (
                          <li key={subItemId}>
                            <a
                              className="block pt-4 text-sm font-bold text-dark-gray hover:underline"
                              href={normalizeUrl(subItemSlug)}
                            >
                              {itemTitle}
                            </a>
                          </li>
                        )
                      )}
                    </ul>
                  </>
                ) : (
                  <Button
                    variant="tertiary"
                    as="a"
                    href={`/${slug}`}
                    className="m-0 p-0"
                  >
                    <Button.Text className="p-0 text-lg font-bold">
                      {title}
                    </Button.Text>
                  </Button>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </section>
  );
}
